import React, { useState, useEffect } from "react";
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Select, MenuItem, FormControl, InputLabel, IconButton, Box, Typography, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { Sex } from '../../api/unit/Sex';
import { Role } from '../../api/unit/Role';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { OutletContextType } from '../root';
import { userManagerInitialize, userUpdate, sendMessage } from '../../api/api';
import {
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

type User = {
    picture_url: string;
    user_id: string;
    user_name: string;
    role: Role;
    valid_flag: string;
    sex: string;
    birthday: string;
    age: string;
    paypay_name: string;
    paypay_id: string;
    update_date: string;
    register_date: string;
    isEditing: boolean;
    [key: string]: any;
};

export const UserManagement = () => {
  const navigate = useNavigate();
  const [reloadTime, setReloadTime] = useState<Date>();
  const { userId } = useOutletContext<OutletContextType>();
  const [users, setUsers] = useState<User[]>([]);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [sortBy, setSortBy] = useState<string>("user_id");
  const [openResultDialog, setOpenResultDialog] = useState<boolean>(false);
  const [openMessageDialog, setOpenMessageDialog] = useState<boolean>(false);
  const [sendMessageTargetUserId, setSendMessageTargetUserId] = useState<string>("");
  const [sendMessageText, setSendMessageText] = useState<string>("");
  const [processResultMessage, setProcessResultMessage] = useState<string>('');

  useEffect(() => {
    // 初期表示処理を行いサーバーサイドからユーザーマスタを取得する
    userManagerInitialize(userId).then(response => {
      const useData = response?.tkgReserve0220DetailsObjectLot?.tkgReserve0220DetailsObjectList?.map((userEntity: any) => ({
        picture_url: userEntity.pictureUrl,
        user_id: userEntity.userId,
        user_name: userEntity.userName,
        role: new Role(userEntity.role),
        valid_flag: userEntity.validFlag,
        sex: userEntity.sex,
        birthday: userEntity.birthday,
        age: userEntity.age,
        paypay_name: userEntity.paypayName,
        paypay_id: userEntity.paypayId,
        update_date: userEntity.updateDate,
        register_date: userEntity.registerDate,
        isEditing: false
      }));
      setUsers(useData);  // 状態を更新
    });
  }, [userId, reloadTime]);

  const handleSort = (column: string) => {
    const isAsc = sortBy === column && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortBy(column);
    const sortedUsers = [...users].sort((a, b) => {
      if (a[column] < b[column]) return isAsc ? -1 : 1;
      if (a[column] > b[column]) return isAsc ? 1 : -1;
      return 0;
    });
    setUsers(sortedUsers);
  };

  const handleEdit = (index: number) => {
    const updatedUsers = [...users];
    // 編集前のユーザーの情報を保持
    updatedUsers[index] = { ...users[index], originalData: { ...users[index] } };
    updatedUsers[index].isEditing = true;
    setUsers(updatedUsers);
  };

  const handleSave = (index: number) => {
    const updatedUsers = [...users];
    updatedUsers[index].isEditing = false;
    // リクエストを作成
    const requestData = {
        "user_id" : userId,
        "target_user_id" : updatedUsers[index].user_id,
        "user_name" : updatedUsers[index].user_name,
        "role" : updatedUsers[index].role?.getCode(),
        "valid_flag" : updatedUsers[index].valid_flag,
        "sex" : updatedUsers[index].sex,
        "birthday" : updatedUsers[index].birthday,
        "paypay_name" : updatedUsers[index].paypay_name,
        "paypay_id" : updatedUsers[index].paypay_id
    }
    // ユーザー更新APIを呼び出す
    userUpdate(requestData).then(response => {
      setUsers(updatedUsers);
      setProcessResultMessage(response.message);
      setOpenResultDialog(true);
    });
  };

  const handleCancel = (index: number) => {
    const updatedUsers = [...users];
    // 編集前のデータに戻す
    updatedUsers[index] = { ...updatedUsers[index].originalData, isEditing: false };
    setUsers(updatedUsers);
  };
  
  const handleInputChange = (index: number, field: string, value: string) => {
    const updatedUsers = [...users];
    if (field === "role") {
      updatedUsers[index].role = new Role(value);  // ロール変更の際はRoleオブジェクトを再生成
    } else {
      updatedUsers[index][field as keyof User] = value;
    }
    setUsers(updatedUsers);
  };

  const handleMessageSend = () => {
    const message = sendMessageText.replace(/\r\n|\r/g, "\n"); // 改行コードを統一
    // リクエストを作成
    const requestData = {
      "user_id" : userId,
      "target_user_id" : sendMessageTargetUserId,
      "message": message
    }
    // メッセージ送信APIを呼び出す
    sendMessage(requestData).then(response => {
      setOpenMessageDialog(false)
      setProcessResultMessage(response.message);
      setOpenResultDialog(true);
    });
  };

  return (
    <>
    <Box sx={{ paddingTop:'10vh' }}>
      <Button
          variant="outlined"
          color="primary"
          startIcon={<ArrowBackIcon sx={{transition: 'transform 0.3s ease'}} />}
          onClick={() => navigate("/mng/portal")}
          sx={{
            ':hover': {
                backgroundColor: '#1565c0',
                color: 'white'
            }
          }}
      >
          ポータルに戻る
      </Button>
      <TableContainer sx={{ padding: 3 }}>
        <Typography variant="h4" gutterBottom>
          ユーザーマスタ
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell onClick={() => handleSort("user_name")}>名前</TableCell>
              <TableCell onClick={() => handleSort("role_cd")}>ロール</TableCell>
              <TableCell onClick={() => handleSort("valid_flag")}>有効フラグ</TableCell>
              <TableCell onClick={() => handleSort("sex")}>性別</TableCell>
              <TableCell onClick={() => handleSort("birthday")}>誕生日</TableCell>
              <TableCell onClick={() => handleSort("age")}>年齢</TableCell>
              <TableCell onClick={() => handleSort("paypay_name")}>PayPay名</TableCell>
              <TableCell onClick={() => handleSort("paypay_id")}>PayPayID</TableCell>
              <TableCell onClick={() => handleSort("update_date")}>更新日時</TableCell>
              <TableCell onClick={() => handleSort("register_date")}>登録日時</TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user, index) => (
              <TableRow key={user.user_id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                    {user.isEditing ? (
                        <TextField label="ユーザー名" value={user.user_name} onChange={(e) => handleInputChange(index, "user_name", e.target.value)} />
                    ) : (
                        <Box display="flex" alignItems="center">
                            {user.picture_url ? (<img src={user.picture_url}  alt="User" style={{ width: 40, height: 40, borderRadius: "50%", marginRight: 8 }} />) : (<></>)}
                            <span>{user.user_name}</span>
                        </Box>
                    )}
                </TableCell>
                <TableCell>
                  {user.isEditing ? (
                    <FormControl fullWidth>
                      <InputLabel>ロール</InputLabel>
                      <Select value={user.role.getCode()} onChange={(e) => handleInputChange(index, "role", e.target.value)}>
                        <MenuItem value="00">{Role.RoleMap["00"].name}</MenuItem>
                        <MenuItem value="01">{Role.RoleMap["01"].name}</MenuItem>
                        <MenuItem value="02">{Role.RoleMap["02"].name}</MenuItem>
                        <MenuItem value="03">{Role.RoleMap["03"].name}</MenuItem>
                        <MenuItem value="99">{Role.RoleMap["99"].name}</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    user.role.getName()
                  )}
                </TableCell>
                <TableCell>
                  {user.isEditing ? (
                    <FormControl fullWidth>
                      <InputLabel>有効フラグ</InputLabel>
                      <Select value={user.valid_flag} onChange={(e) => handleInputChange(index, "valid_flag", e.target.value)}>
                        <MenuItem value="1">有効</MenuItem>
                        <MenuItem value="0">無効</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <Box sx={{ textAlign: "center", color: user.valid_flag === "1" ? "black" : "white", backgroundColor: user.valid_flag === "1" ? "white" : "gray"  }}>
                      {user.valid_flag === "1" ? "有効" : user.valid_flag === "0" ? "無効" : "不明"}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {user.isEditing ? (
                    <FormControl fullWidth>
                      <InputLabel>性別</InputLabel>
                      <Select value={user.sex} onChange={(e) => handleInputChange(index, "sex", e.target.value)}>
                        <MenuItem value={Sex.Women}>女性</MenuItem>
                        <MenuItem value={Sex.Man}>男性</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <span>{user.sex === Sex.Women ? "女性" : user.sex === Sex.Man ? "男性" : "不明"}</span>
                  )}
                </TableCell>
                <TableCell>{user.isEditing ? <TextField type="date" value={user.birthday} onChange={(e) => handleInputChange(index, "birthday", e.target.value)} /> : user.birthday}</TableCell>
                <TableCell>{user.age}</TableCell>
                <TableCell>{user.isEditing ? <TextField label="PayPay名" value={user.paypay_name} onChange={(e) => handleInputChange(index, "paypay_name", e.target.value)} /> : user.paypay_name}</TableCell>
                <TableCell>{user.isEditing ? <TextField label="PayPay ID" value={user.paypay_id} onChange={(e) => handleInputChange(index, "paypay_id", e.target.value)} /> : user.paypay_id}</TableCell>
                <TableCell>{user.update_date}</TableCell>
                <TableCell>{user.register_date}</TableCell>
                <TableCell>
                  {user.isEditing ? (
                    <>
                      <Button onClick={() => handleSave(index)} variant="contained" color="primary" sx={{ marginRight: 1, paddingBottom: 1 }}>
                        保存
                      </Button>
                      <Button onClick={() => handleCancel(index)} variant="outlined" color="secondary">
                        破棄
                      </Button>
                    </>
                  ) : (
                    <IconButton onClick={() => handleEdit(index)}>
                      <Button variant="outlined">編集</Button>
                    </IconButton>
                  )}
                </TableCell>
                <TableCell>
                  <Button onClick={() => {setOpenMessageDialog(true);setSendMessageTargetUserId(user.user_id);}} variant="outlined" color="success" sx={{ marginRight: 1 }}>
                    メッセージ
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>

    {/* メッセージ入力ダイアログ */}
    <Dialog
      open={openMessageDialog}
      onClose={() => setOpenMessageDialog(false)}
      sx={{
        '& .MuiDialog-paper': {
          width: '600px',
          height: '400px',
          maxWidth: 'none'
        }
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'primary.main',
          }}
        >
          <InfoIcon fontSize="large" />
          <Typography variant="h6">送信内容</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        メッセージ
        <TextField
          multiline
          minRows={3}
          placeholder="メッセージを入力してください"
          variant="outlined"
          fullWidth
          onChange={(e) => setSendMessageText(e.target.value)}
          sx={{
            borderRadius: 2,
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleMessageSend()} variant="contained" color="success">
          送る
        </Button>
        <Button onClick={() => setOpenMessageDialog(false)} variant="outlined" color="error">
          閉じる
        </Button>
      </DialogActions>
    </Dialog>

    {/* 処理結果通知ダイアログ */}
    <Dialog open={openResultDialog} onClose={() => setOpenResultDialog(false)}>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'primary.main',
            }}
          >
            <InfoIcon fontSize="large" />
            <Typography variant="h6">処理結果</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <pre>{processResultMessage}</pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {setOpenResultDialog(false);setReloadTime(new Date())}} variant="contained" color="primary">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
